import {
  MeasurementSystem,
  PointAndPlaneFragment,
} from '@warebee/frontend/data-access-api-graphql';
import _ from 'lodash';
import { nanoid } from 'nanoid';
import React, { useState } from 'react';
import { Layer } from 'react-konva';
import {
  useRecoilValue,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';
import { getQualitativeColor } from '../../common/color.helper';
import { toHumanReadableTime } from '../../common/dateTimeHelper';
import useGetNamedColor from '../../common/useGetNamedColor';
import EventFeature from '../../layout/features/EventFeature';
import {
  VisibilityMode,
  getCompactCircleStyle,
  getEventPosition,
} from '../../layout/features/route/sideOffset';
import RouteFeatureLight from '../../layout/features/RouteFeatureLight';
import { stageStateById } from '../../layout/stage/stage.state';
import { warehouseMeasurementSystem } from '../../store/warehouse.state';
import { actualitySelectedEvent } from '../store/actuality.hq.state';
import {
  actualityAggregatedScopedEvents,
  actualityAggregatedScopedEventsRoute,
  actualityAggregatedStatsGroupBy,
  actualityAggregatedStatsSelectedSubGroup,
} from '../store/actuality.kpi.state';
import { feedSelectedPickListId } from '../store/feed.state';

const ANIMATION_EVENT_THRESHOLD = 200;

const ActivityRoutesLayer: React.FC = () => {
  // ALL HOOKS MUST BE AT THE TOP - NO CONDITIONAL RETURNS BEFORE THIS POINT
  const [hoveredEventId, setHoveredEventId] = useState<string | null>(null);
  const [visibilityMode, setVisibilityMode] =
    useState<VisibilityMode>('normal');

  const jobId = useRecoilValue(feedSelectedPickListId);
  const stageState = useRecoilValue(stageStateById('viewer-area-view'));
  const measureSystem = useRecoilValue(warehouseMeasurementSystem);
  const scaleFactor =
    measureSystem === MeasurementSystem.IMPERIAL ? 0.25 : 0.25;
  const groupByField = useRecoilValue(actualityAggregatedStatsGroupBy);
  const selectedSubGroup = useRecoilValue(
    actualityAggregatedStatsSelectedSubGroup,
  );
  const scopedRoutes = useRecoilValueLoadable(
    actualityAggregatedScopedEventsRoute(selectedSubGroup),
  );

  const selectedEvent = useRecoilValue(actualitySelectedEvent);
  const scopedEventsLoadable = useRecoilValueLoadable(
    actualityAggregatedScopedEvents(selectedSubGroup),
  );
  const getNamedColor = useGetNamedColor();
  const setSelectedEvent = useSetRecoilState(actualitySelectedEvent);

  // ALL CONDITIONAL LOGIC AND EARLY RETURNS AFTER HOOKS
  const showAllEvents =
    !!selectedSubGroup?.['jobId'] || !!selectedSubGroup?.['eventHour'];

  // Early return after all hooks are declared
  if (scopedRoutes.state !== 'hasValue') return null;

  const scopedEvents = scopedEventsLoadable.contents;
  const tooManyEvents =
    Array.isArray(scopedEvents) &&
    scopedEvents.length > ANIMATION_EVENT_THRESHOLD;
  const shouldUseCompactMode = tooManyEvents;
  const activeMode = shouldUseCompactMode ? 'compact' : visibilityMode;

  let routes = [];
  if (selectedEvent) {
    const eventRoute = _.flatten(
      selectedEvent.route as any as PointAndPlaneFragment[][],
    );
    routes = [eventRoute];
  } else {
    routes = scopedRoutes.getValue();
  }

  const handleEventHover = (eventId: string | null) => {
    setHoveredEventId(eventId);
  };

  const handleEventSelection = (eventId: string | null) => {
    console.log('Event selection changed:', eventId);
    if (eventId) {
      // Find the event by ID and set it as selected
      const eventToSelect = scopedEvents?.find(e => e.eventId === eventId);
      if (eventToSelect) {
        setSelectedEvent(eventToSelect);
      }
    } else {
      // Deselect by setting to null
      setSelectedEvent(null);
    }
  };

  const visibleEvents = _.filter(
    scopedEvents,
    e => !_.isNil(e.layoutLocationId),
  );

  return (
    <Layer>
      {/* Render routes first so they appear behind the event */}
      {_.map(visibleEvents, event => {
        const eventColors = getNamedColor(event['agentId']);
        const eventColor = eventColors?.[0];

        const eventRoute = _.flatten(
          event.route as any as PointAndPlaneFragment[][],
        );

        // console.log('event', event);
        return (
          <RouteFeatureLight
            key={`job-route-${nanoid()}`}
            points={eventRoute}
            colorRoute={eventColors[1]}
            colorRouteBackground={eventColors[0]}
            animate={!tooManyEvents}
            direction="FORWARD"
            side={event.locationSide}
            eventType={event.eventType}
          />
          // <RouteFeature
          //   key={`job-route-${jobId}`}
          //   waypoints={_.map(waypoints, (w, i) => ({
          //     ...w,
          //     label: w.type === WaypointType.LOCATION ? `${index++}` : null,
          //   }))}
          //   animate
          //   showLabels
          //   scale={scaleFactor / stageState.scale}
          // />
        );
      })}
      {showAllEvents && (
        <>
          {_.map(visibleEvents, (event, index) => {
            const eventColors = getNamedColor(event['agentId']);
            const eventPosition = getEventPosition(event, index, activeMode);
            const circleStyle = getCompactCircleStyle(
              event.eventType,
              eventColors[0],
              activeMode === 'compact',
            );

            const isHovered = hoveredEventId === event.eventId;
            const isActiveEvent = selectedEvent?.eventId === event.eventId;
            const shouldDim = hoveredEventId && !isHovered && !isActiveEvent;

            return (
              <EventFeature
                key={`event-${event.eventId}`}
                event={{ ...event, x: eventPosition.x, y: eventPosition.y }}
                eventType={event.eventType}
                animate={false}
                subtitle={toHumanReadableTime(event.eventEndTime)}
                colorCircle={
                  getQualitativeColor(event?.agentId, 'dimension')[0] ?? 'red'
                }
                colorIcon={
                  getQualitativeColor(event?.agentId, 'dimension')[1] ?? 'red'
                }
                colorSelection={
                  getQualitativeColor(event?.agentId, 'dimension')[1] ?? 'red'
                }
                isSelected={selectedEvent?.eventId === event.eventId}
                showLabels
                // showTooltip
                colorFields={['agentId']}
                supportedColorBy={['bucket']}
                baseOpacity={shouldDim ? 0.2 : 0.8}
                onHover={handleEventHover}
                onSelection={handleEventSelection}
                // animateStartEnd
                // tooltipData={{
                //   'Agent Type': event?.agentType ?? 'Unknown',
                //   'Agent ID': event?.agentId ?? 'Unknown',
                // }}
              />
            );
          })}
        </>
      )}
      {/* Render event feature on top */}
      {selectedEvent?.layoutLocationId && (
        <EventFeature
          key={`event-${selectedEvent.eventId}`}
          event={{
            ...selectedEvent,
            ...getEventPosition(selectedEvent, 0, activeMode), // Use same position logic
          }}
          eventType={selectedEvent.eventType}
          animate={true}
          title={selectedEvent.agentId}
          subtitle={toHumanReadableTime(selectedEvent.eventEndTime)}
          colorIcon={
            getQualitativeColor(selectedEvent?.agentId, 'dimension')[1] ?? 'red'
          }
          colorSelection={
            getQualitativeColor(selectedEvent?.agentId, 'dimension')[1] ?? 'red'
          }
          isSelected
          selectedAnimation="marching-ants"
          // animateStartEnd={true}
          showLabels
          showTooltip
          baseOpacity={0.8}
          onHover={handleEventHover}
          onSelection={handleEventSelection}
          // tooltipData={{
          //   'Agent Type': selectedEvent?.agentType ?? 'Unknown',
          //   'Agent ID': selectedEvent?.agentId ?? 'Unknown',
          // }}
        />
      )}
    </Layer>
  );
};

export default ActivityRoutesLayer;
